/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'

import {
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiTitle,
  htmlIdGenerator,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { ApiKeyResponse, RoleAssignments } from '@modules/cloud-api/v1/types'
import { useConfig } from '@modules/cui/ConfigContext'
import { useOrganizationId } from '@modules/permissions-lib/hooks'
import {
  useCreateApiKeyMutation,
  useGetCurrentUserApiKeysQuery,
  useGetAllUsersApiKeysQuery,
} from '@modules/api-keys-lib'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import GenerateKeyForm, { GenerateKeyFormFooter } from './GenerateKeyForm'
import GeneratedApiKey from './GeneratedApiKey'
import messages from './messages'
import {
  DEFAULT_EXPIRATION_ADMINCONSOLE,
  DEFAULT_EXPIRATION_USERCONSOLE,
  diffFromExpirationToToday,
} from './lib'

import type { WrappedComponentProps } from 'react-intl'

interface Props extends WrappedComponentProps {
  onCancel?: () => void
}

type Step = 'create-step' | 'download-step'

const GenerateKeyModal: React.FC<Props> = ({ onCancel }) => {
  const { regionId } = useParams<{ regionId?: string }>()
  const { organizationId } = useOrganizationId()
  const navigate = useNavigate()

  const appName = useConfig('APP_NAME')
  const appPlatform = useConfig('APP_PLATFORM')

  const [_, flags] = useFlagsWhenLoaded()
  const isUIAMEnabled = flags.uiamUi

  const manageAllUsersApiKeys = regionId !== undefined

  // Query hooks - use appropriate API keys based on context
  const { data: currentUserApiKeys = [] } = useGetCurrentUserApiKeysQuery({
    enabled: !manageAllUsersApiKeys,
  })
  const { data: allUsersApiKeys = [] } = useGetAllUsersApiKeysQuery({
    enabled: manageAllUsersApiKeys,
  })
  const apiKeys = manageAllUsersApiKeys ? allUsersApiKeys : currentUserApiKeys

  const createApiKeyMutation = useCreateApiKeyMutation()

  const isAdminconsole = appName === 'adminconsole'
  // All Adminconsole environments (ESS, GovCloud), except Userconsole and ECE
  const isAnySaasAdminconsole = appPlatform === 'saas' && isAdminconsole

  const defaultExpiration = isAnySaasAdminconsole
    ? DEFAULT_EXPIRATION_ADMINCONSOLE
    : DEFAULT_EXPIRATION_USERCONSOLE

  const showRoleAssignments = !isAdminconsole // set API keys roles only in Userconsole
  const showNeverExpirationOption = !isAnySaasAdminconsole // only show never expire option in Userconsole and ECE

  const [apiKey, setApiKey] = useState<ApiKeyResponse | null>(null)
  const [currentStep, setCurrentStep] = useState<Step>('create-step')
  const [invalidKeyName, setInvalidKeyName] = useState(false)
  const [name, setName] = useState<string | null>('')
  const [keyError, setKeyError] = useState<JSX.Element[]>([])
  const [expiration, setExpiration] = useState<string | null>(
    diffFromExpirationToToday(defaultExpiration, moment()),
  )
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignments>({})

  const onChangeName = (e: React.SyntheticEvent) => {
    // @ts-ignore
    setName(e.target.value)
  }

  const onChangeExpiration = (newExpiration: string | null): void => {
    setExpiration(newExpiration)
  }

  const onChangeRoleAssignments = (newRoleAssignments: RoleAssignments) => {
    setRoleAssignments(newRoleAssignments)
  }

  const onCancelHandler = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/keys')
    }
  }

  const generateApiKeyHandler = () => {
    const isNameValid =
      apiKeys.length > 0
        ? apiKeys.filter((key: ApiKeyResponse) => key.description === name).length === 0
        : true

    if (!name || name.length < 1) {
      setName(null)
      setInvalidKeyName(true)
      setKeyError([
        <FormattedMessage key={messages.keyNameEmptyError.id} {...messages.keyNameEmptyError} />,
      ])
      return
    }

    if (!isNameValid) {
      setName('')
      setInvalidKeyName(true)
      setKeyError([<FormattedMessage key={messages.keyNameError.id} {...messages.keyNameError} />])
      return
    }

    createApiKeyMutation.mutate(
      { name, expiration, roleAssignments: showRoleAssignments ? roleAssignments : undefined },
      {
        onSuccess: (response) => {
          setApiKey(response)
          setName(response.description)
          setCurrentStep('download-step')
        },
        onError: (error) => {
          const errorMessage = error.errors[0]?.message || 'An error occurred'
          setName('')
          setInvalidKeyName(true)
          setKeyError([
            <FormattedMessage
              key={`${step.title.id}-error`}
              id={`${step.title.id}-error`}
              defaultMessage={errorMessage}
            />,
          ])
        },
      },
    )
  }

  const createStep = {
    title: messages.createApiKey,
    body: (
      <GenerateKeyForm
        organizationId={organizationId}
        roleAssignments={roleAssignments}
        onChangeName={onChangeName}
        onChangeExpiration={onChangeExpiration}
        onChangeRoleAssignments={onChangeRoleAssignments}
        onNext={generateApiKeyHandler}
        invalidKeyName={invalidKeyName}
        errors={keyError}
        defaultExpiration={defaultExpiration}
        showRoleAssignments={showRoleAssignments}
        showNeverExpirationOption={showNeverExpirationOption}
      />
    ),
    footer: (
      <GenerateKeyFormFooter
        generateKeyRequestInProgress={createApiKeyMutation.isLoading}
        onCancel={onCancelHandler}
        onNext={generateApiKeyHandler}
        roleAssignments={roleAssignments}
        showRoleAssignments={showRoleAssignments}
      />
    ),
  }

  const step =
    currentStep === 'create-step' || apiKey === null
      ? createStep
      : {
          title: messages.createApiKey,
          body: <GeneratedApiKey apiKey={apiKey} />,
          footer: null,
        }

  const stepTitle =
    typeof step.title === `string` ? step.title : <FormattedMessage {...step.title} />

  const flyoutTitleId = htmlIdGenerator()('modalTitle')

  return (
    <EuiFlyout
      maxWidth={!isUIAMEnabled ? '57rem' : undefined}
      size={isUIAMEnabled ? 'm' : undefined}
      onClose={onCancelHandler}
      aria-labelledby={flyoutTitleId}
    >
      <EuiFlyoutHeader data-test-id='generateKey-modal' hasBorder={true}>
        <EuiTitle>
          <h2 id={flyoutTitleId}>{stepTitle}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>{step.body}</EuiFlyoutBody>
      {step.footer && <EuiFlyoutFooter>{step.footer}</EuiFlyoutFooter>}
    </EuiFlyout>
  )
}

export default injectIntl(GenerateKeyModal)
